/* eslint-disable */
import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { sendSlack, type SlackSendRequest } from '../../services/api';

interface SendSlackButtonProps {
  markdownContent: string;
  channel?: string;
  user?: string;
  release: string;
  disabled?: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const SendSlackButton: React.FC<SendSlackButtonProps> = ({
  markdownContent,
  channel,
  user,
  release,
  disabled = false,
  onSuccess,
  onError,
}) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = markdownContent.trim().length > 0 && (channel || user);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!sending) {
      setOpen(false);
      setError(null);
    }
  };

  const handleConfirm = async () => {
    if (!canSend) {
      setError('Please provide markdown content and recipient');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const request: SlackSendRequest = {
        markdown_content: markdownContent,
        channel,
        user,
        release,
      };

      const response = await sendSlack(request);

      if (response.success) {
        setOpen(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.message || 'Failed to send message');
        if (onError) {
          onError(response.message || 'Failed to send message');
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<SendIcon />}
        onClick={handleOpen}
        disabled={disabled || !canSend}
        fullWidth
      >
        Send to Slack
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Release Report to Slack</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to send this release report to Slack?
            {channel && (
              <>
                <br />
                <strong>Channel:</strong> {channel}
              </>
            )}
            {user && (
              <>
                <br />
                <strong>User:</strong> {user}
              </>
            )}
            <br />
            <strong>Release:</strong> {release}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={sending || !canSend}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
